// Match Goals + Match Cards market table — types + shaping (Phase 3).
//
// A responsive odds table: for each goals/cards line, an Over and an Under row
// showing the selected bookmaker's price + implied %, the model's price + %, the
// value edge (model implied − market implied), and the best price across all
// bookmakers. Same structure for both markets — only the dataset differs. Pure +
// framework-free; the component owns the two responsive layouts (wide/narrow) and
// the Best column gate. Reuses the market types defined for Value Indicator.

import type {
  GoalsCardsData,
  ScopeMarket,
  WinnerData,
  Outcome3,
  PlayerMarketData,
} from "./valueIndicator.js";

/** The goals/cards lines, in display order. */
export const MARKET_LINES = ["0.5", "1.5", "2.5", "3.5"] as const;

/** Bookmaker badge info (abbreviation + brand colour), keyed by name. */
export const BK_INFO: Record<string, { abbr: string; colour: string }> = {
  Pinnacle: { abbr: "PN", colour: "#1c3f6e" },
  Betfair: { abbr: "BF", colour: "#f5c000" },
  Ladbrokes: { abbr: "LD", colour: "#e30613" },
  "Paddy Power": { abbr: "PP", colour: "#00a651" },
};

/** The best price across bookmakers for one line/direction. */
export interface BestPrice {
  odds: number;
  abbr: string;
  colour: string;
}

/** One shaped market row (one line, one direction). */
export interface MarketRow {
  /** Cross-link key, e.g. "over_1.5" / "under_2.5". */
  dataLine: string;
  /** Row label, e.g. "O/1.5" / "U/2.5". */
  label: string;
  /** Selected bookmaker's decimal odds. */
  mktOdds: number;
  /** Selected bookmaker's implied probability (%). */
  mktImp: number;
  /** Model's decimal odds. */
  modelOdds: number;
  /** Model's implied probability (%). */
  modelImp: number;
  /** Value edge (model implied − market implied), formatted "x.xx%". */
  edgeStr: string;
  /** Edge colour: green positive, red negative, grey ~flat. */
  edgeCol: string;
  /** Best price across all bookmakers for this line/direction. */
  best: BestPrice;
  /** Zebra striping: true on odd row indices (`bm-row--alt`). */
  alt: boolean;
}

const imp = (odds: number): number => 100 / odds;

function edgeVal(modelOdds: number, mktOdds: number): {
  str: string;
  col: string;
} {
  const diff = imp(modelOdds) - imp(mktOdds);
  return {
    str: diff.toFixed(2) + "%",
    col: diff > 0.01 ? "#2ecc71" : diff < -0.01 ? "#e74c3c" : "#888",
  };
}

// Best decimal odds across all bookmakers for a line + direction (0 = over,
// 1 = under), matching the legacy `bestFor`.
function bestFor(scopeData: ScopeMarket, line: string, dir: number): BestPrice {
  let best = 0;
  let info = { abbr: "??", colour: "#555" };
  Object.keys(scopeData.bookmakers).forEach((bkName) => {
    const odds = scopeData.bookmakers[bkName]![line]![dir]!;
    if (odds > best) {
      best = odds;
      info = BK_INFO[bkName] ?? info;
    }
  });
  return { odds: best, abbr: info.abbr, colour: info.colour };
}

/**
 * Shape a Match Goals / Match Cards table for a fixture + scope + bookmaker.
 * Rows are emitted Under-then-Over per line (matching the legacy order), with a
 * running zebra index. Falls back to the "Arsenal v Everton" fixture / "Match"
 * scope / "Pinnacle" book, like the legacy renderer. Returns null if unseeded.
 */
export function shapeMarketTable(
  data: GoalsCardsData,
  fixture: string,
  scope: string,
  bookmaker: string
): MarketRow[] | null {
  const fixData = data[fixture] ?? data["Arsenal v Everton"];
  if (!fixData) return null;
  const scopeData = fixData[scope] ?? fixData["Match"];
  if (!scopeData) return null;
  const bkData =
    scopeData.bookmakers[bookmaker] ?? scopeData.bookmakers["Pinnacle"];
  const model = scopeData.model;
  if (!bkData || !model) return null;

  const rows: MarketRow[] = [];
  let idx = 0;
  // Per line: Under (dir 1) then Over (dir 0), matching the legacy layout.
  for (const line of MARKET_LINES) {
    for (const dir of [1, 0]) {
      const mktOdds = bkData[line]![dir]!;
      const modelOdds = model[line]![dir]!;
      const edg = edgeVal(modelOdds, mktOdds);
      rows.push({
        dataLine: (dir === 0 ? "over" : "under") + "_" + line,
        label: (dir === 0 ? "O/" : "U/") + line,
        mktOdds,
        mktImp: imp(mktOdds),
        modelOdds,
        modelImp: imp(modelOdds),
        edgeStr: edg.str,
        edgeCol: edg.col,
        best: bestFor(scopeData, line, dir),
        alt: idx % 2 === 1,
      });
      idx++;
    }
  }
  return rows;
}

// ── Winner (1x2) ────────────────────────────────────────────────────────────

/** The 1x2 outcomes, in display order. */
const WINNER_OUTCOMES = ["home", "draw", "away"] as const;
type WinnerOutcome = (typeof WINNER_OUTCOMES)[number];

/** Row labels for the three outcomes (home/away are team abbreviations). */
export interface WinnerLabels {
  home: string;
  draw: string;
  away: string;
}

// Best decimal odds across bookmakers for a 1x2 outcome.
function bestForOutcome(
  bookmakers: Record<string, Outcome3>,
  outcome: WinnerOutcome
): BestPrice {
  let best = 0;
  let info = { abbr: "??", colour: "#555" };
  Object.keys(bookmakers).forEach((bkName) => {
    const odds = bookmakers[bkName]![outcome];
    if (odds > best) {
      best = odds;
      info = BK_INFO[bkName] ?? info;
    }
  });
  return { odds: best, abbr: info.abbr, colour: info.colour };
}

/**
 * Shape the Winner (1x2) table — three outcome rows (Home / Draw / Away) in the
 * same MarketRow shape as the goals/cards table, so it reuses <MarketTable>. The
 * home/away labels are team abbreviations resolved by the caller (the component,
 * from the legacy window.TEAM_ABBR, keeping this window-free). `dataLine` is the
 * outcome key ("home" | "draw" | "away"). Returns null if unseeded.
 */
export function shapeWinnerTable(
  data: WinnerData,
  fixture: string,
  bookmaker: string,
  labels: WinnerLabels
): MarketRow[] | null {
  const fixData = data[fixture] ?? data["Arsenal v Everton"];
  if (!fixData) return null;
  const bkData = fixData.bookmakers[bookmaker] ?? fixData.bookmakers["Pinnacle"];
  const model = fixData.model;
  if (!bkData || !model) return null;

  return WINNER_OUTCOMES.map((outcome, idx) => {
    const mktOdds = bkData[outcome];
    const modelOdds = model[outcome];
    const edg = edgeVal(modelOdds, mktOdds);
    return {
      dataLine: outcome,
      label: labels[outcome],
      mktOdds,
      mktImp: imp(mktOdds),
      modelOdds,
      modelImp: imp(modelOdds),
      edgeStr: edg.str,
      edgeCol: edg.col,
      best: bestForOutcome(fixData.bookmakers, outcome),
      alt: idx % 2 === 1,
    };
  });
}

// ── Player markets (Goals / Cards) ──────────────────────────────────────────

/** One shaped player row. Same fields as MarketRow, keyed by player name. */
export interface PlayerRow {
  /** Player name — both the row label and the `data-player` cross-link key. */
  player: string;
  mktOdds: number;
  mktImp: number;
  modelOdds: number;
  modelImp: number;
  edgeStr: string;
  edgeCol: string;
  best: BestPrice;
  alt: boolean;
}

const PLAYER_BOOKMAKERS = ["Pinnacle", "Betfair", "Ladbrokes", "Paddy Power"];

/**
 * Shape a Player Goals / Player Cards table for a fixture + scope + bookmaker +
 * market type (Anytime / First / 2+). One row per player, with the best price
 * across the four bookmakers. Falls back to the default fixture / "Match" scope /
 * "Anytime" type / "Pinnacle" book, like the legacy renderer. Null if unseeded.
 */
export function shapePlayerTable(
  data: PlayerMarketData,
  fixture: string,
  scope: string,
  bookmaker: string,
  marketType: string
): PlayerRow[] | null {
  const fixData = data[fixture] ?? data["Arsenal v Everton"];
  if (!fixData) return null;
  const scopeData = fixData[scope] ?? fixData["Match"];
  if (!scopeData) return null;
  const rows = scopeData[marketType] ?? scopeData["Anytime"];
  if (!rows) return null;

  return rows.map((row, idx) => {
    const mkt = (row[bookmaker] as number) || (row.Pinnacle as number);
    const mdl = row.model;
    let best = 0;
    let info = { abbr: "??", colour: "#555" };
    PLAYER_BOOKMAKERS.forEach((bkName) => {
      const o = row[bkName] as number;
      if (o > best) {
        best = o;
        info = BK_INFO[bkName] ?? info;
      }
    });
    const edg = edgeVal(mdl, mkt);
    return {
      player: row.player,
      mktOdds: mkt,
      mktImp: imp(mkt),
      modelOdds: mdl,
      modelImp: imp(mdl),
      edgeStr: edg.str,
      edgeCol: edg.col,
      best: { odds: best, abbr: info.abbr, colour: info.colour },
      alt: idx % 2 === 1,
    };
  });
}
